import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from '@lib/useTypedTranslation';
import moment from 'moment';
import * as am4charts from '@amcharts/amcharts4/charts';

import { exportDeviceDataUsageChartAction, IDevicesDataUsage } from '../../../../services/core/performance';
import { getFormattedTitle, getPerformanceDataUsageDevicesPath, getTimeZoneOffset, getTitle } from './lib';
import { getUserTimeZone } from '../../../../lib/timeZone';
import { useWorldAction } from '../../../../lib/useWorldAction';
import { Filter } from '../../../../components/timePeriodAndFilterPicker/useFilterPicker';
import { drawYAxis } from '../../../../lib/dataUsageChart';
import { StackedColumnsDateChart } from '../../../../components/chart/stackedColumnsDateChart';
import { useDataUsageSeriesSwitch } from '../../../../components/chart/useDataUsageSeriesSwitch';
import { drawRelativeXAxisLabels } from '../../../../components/chart/lib';
import { formatTooltipDay } from '../../../../components/chart/tooltips';
import { useWorldRoutes } from '../../../../routes/parts/allWorldRoutes';

interface IProps {
  data: IDevicesDataUsage[],
  filter: Filter,
  days: number,
  onMount: (chart: am4charts.XYChart) => am4charts.XYChart
}

export function CorePerformanceDataUsage(props: IProps) {
  const { filter } = props;
  const exportChartData = useWorldAction(exportDeviceDataUsageChartAction);
  const ns = 'translation';
  const { t } = useTranslation(['performance', ns, 'timeState', 'filters']);
  const title = getTitle(filter, props.days, t);
  const history = useHistory();
  const routes = useWorldRoutes();
  const timeZoneOffset = getTimeZoneOffset(getUserTimeZone());

  const {
    buttons: dataUsageSwitchButtons,
    mobileSeriesVisible,
    wifiSeriesVisible,
    selectedType
  } = useDataUsageSeriesSwitch();

  const onBarClick = (dataContext: { mobile: number, wifi: number, date: number }) => {
    if (!dataContext) { return; }
    const value = selectedType === 'total' ? dataContext.mobile + dataContext.wifi : dataContext[selectedType];
    if (!(value > 0)) { return; }
    const utc = moment.utc(dataContext.date).subtract(timeZoneOffset || 0, 'minutes').valueOf();
    history.push(getPerformanceDataUsageDevicesPath(routes, selectedType, utc, filter));
  };

  const formattedTitle = getFormattedTitle(title);

  const onExportClicked = async (): Promise<string> => {
    const params = {
      days: props.days,
      timeZone: getUserTimeZone(),
      homeLocationId: props.filter.homeLocation?.value.id,
      group: props.filter.group?.value,
      zoneId: props.filter.zone?.value.id,
      model: props.filter.model?.value
    };
    return exportChartData(params);
  };

  return (
    <div className= 'core-performance-chart-section'>
      <StackedColumnsDateChart<any>
        data={props.data.map((d) => ({
          date: parseInt(d.date),
          mobile: d.mobile,
          wifi: d.wifi
        }))}
        className={`core-performance-data-usage-chart`}
        series={[{
          visible: mobileSeriesVisible,
          dataKey: 'mobile',
          description: t('MOBILE_DATA', { ns: 'translation' }),
          colour: '#49a1a9'
        },
        {
          visible: wifiSeriesVisible,
          dataKey: 'wifi',
          description: t('WIFI_DATA', { ns: 'translation' }),
          colour: '#2c678c'
        }]}
        totalDescription={t('TOTAL_DATA', { ns: 'translation' })}
        days={props.days}
        cypressId="corePerformanceDataUsage"
        title={title}
        onMount={props.onMount}
        drawYAxis={(chart, axis, enabledSeries) => drawYAxis(chart.data, axis, enabledSeries)}
        amchartsYAxisNumberFormat="#.00b"
        buttons={dataUsageSwitchButtons}
        export={{
          filename: `${formattedTitle}.${moment.utc().toISOString()}.csv`,
          onExportClicked: () => onExportClicked(),
          csv: true
        }}
        xAxisType={'date'}
        drawXAxis={(_, xAxis) => drawRelativeXAxisLabels(xAxis as am4charts.DateAxis, props.days, timeZoneOffset)}
        formatTooltipDate={formatTooltipDay}
        hasLegend={true}
        onBarClick={onBarClick}
        useAxisTooltip
      />
    </div>
  );
}
